// =============================================================
// OnboardingPage  Multi-step Business Onboarding Wizard
// Phase 2: Complete local business setup flow
// =============================================================

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { businessApi, Business } from '../lib/api';
import { IconEdit, IconCheck } from '../components/icons';

interface ServiceItem {
  id?: number;
  name: string;
  description: string;
}

interface TargetLocItem {
  id?: number;
  name: string;
  city: string;
  state: string;
  country: string;
}

const INDUSTRY_PRESETS = [
  'Events & Entertainment',
  'Events & Decoration',
  'Home Services & Remodeling',
  'Dental & Healthcare',
  'Automotive & Detailing',
  'Restaurant & Hospitality',
  'Legal & Financial Services',
  'Real Estate & Property',
  'Fitness & Wellness',
  'Retail & Local Store',
];

const PRESET_SERVICES_MAP: Record<string, string[]> = {
  'Events & Entertainment': ['LED Wall Rental', 'LED Screen Rental', 'Event LED Display', 'AV / Sound System Setup'],
  'Events & Decoration': ['Wedding Tent Rental', 'Wedding Decoration', 'Stage Decoration', 'Event Furniture Rental'],
  'Home Services & Remodeling': ['Roof Repair', 'Kitchen Remodeling', 'Plumbing Service', 'HVAC Installation'],
  'Dental & Healthcare': ['Teeth Whitening', 'Dental Implants', 'Orthodontics & Braces', 'Routine Checkup'],
  'Automotive & Detailing': ['Ceramic Coating', 'Paint Protection Film (PPF)', 'Full Car Detailing', 'Dent Repair'],
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const params = useParams<{ businessId?: string }>();
  const editingBusinessId = params.businessId ? parseInt(params.businessId, 10) : undefined;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!editingBusinessId);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Business info
  const [businessName, setBusinessName] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');

  // Step 2: Industry
  const [primaryIndustry, setPrimaryIndustry] = useState('');
  const [primaryCategory, setPrimaryCategory] = useState('');
  const [secondaryCategories, setSecondaryCategories] = useState<string[]>([]);
  const [secCategoryInput, setSecCategoryInput] = useState('');

  // Step 3: Services
  const [services, setServices] = useState<ServiceItem[]>([
    { name: '', description: '' },
  ]);

  // Step 4: Target locations
  const [targetLocations, setTargetLocations] = useState<TargetLocItem[]>([
    { name: '', city: '', state: '', country: 'India' },
  ]);

  // Step 5: Physical location
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('India');
  const [postalCode, setPostalCode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [existingLocationName, setExistingLocationName] = useState('Main Location');

  // Load existing business if editing/continuing onboarding
  useEffect(() => {
    if (!editingBusinessId) return;

    setInitialLoading(true);
    businessApi
      .get(editingBusinessId)
      .then((biz: Business) => {
        setBusinessName(biz.name ?? '');
        setWebsite(biz.website ?? '');
        setPhone(biz.phone ?? '');
        setEmail(biz.email ?? '');
        setDescription(biz.description ?? '');

        if (biz.settings) {
          setPrimaryIndustry(biz.settings.primaryIndustry ?? biz.industry ?? '');
          setPrimaryCategory(biz.settings.primaryCategory ?? '');
          setSecondaryCategories(biz.settings.secondaryCategories ?? []);
        } else if (biz.industry) {
          setPrimaryIndustry(biz.industry);
        }

        if (biz.services && biz.services.length > 0) {
          const seen = new Set<string>();
          const deduped: ServiceItem[] = [];
          for (const s of biz.services) {
            const key = s.name.toLowerCase().trim();
            if (!seen.has(key)) {
              seen.add(key);
              deduped.push({ id: s.id, name: s.name, description: s.description ?? '' });
            }
          }
          setServices(deduped.length > 0 ? deduped : [{ name: '', description: '' }]);
        }

        if (biz.targetLocations && biz.targetLocations.length > 0) {
          const seen = new Set<string>();
          const deduped: TargetLocItem[] = [];
          for (const t of biz.targetLocations) {
            const key = t.name.toLowerCase().trim();
            if (!seen.has(key)) {
              seen.add(key);
              deduped.push({
                id: t.id,
                name: t.name,
                city: t.city ?? '',
                state: t.state ?? '',
                country: t.country ?? 'India',
              });
            }
          }
          setTargetLocations(
            deduped.length > 0
              ? deduped
              : [{ name: '', city: '', state: '', country: 'India' }]
          );
        }

        if (biz.locations && biz.locations.length > 0) {
          const loc = biz.locations[0];
          if (loc.name) setExistingLocationName(loc.name);
          setAddress(loc.address ?? '');
          setCity(loc.city ?? '');
          setState(loc.state ?? '');
          setCountry(loc.country ?? 'India');
          setPostalCode(loc.postalCode ?? '');
          if (loc.latitude) setLatitude(String(loc.latitude));
          if (loc.longitude) setLongitude(String(loc.longitude));
        }

        if (biz.onboarding?.currentStep) {
          setCurrentStep(Math.min(biz.onboarding.currentStep, 6));
        }
      })
      .catch((err) => {
        console.error('Failed to load business', err);
        setError('Failed to load business data for onboarding.');
      })
      .finally(() => {
        setInitialLoading(false);
      });
  }, [editingBusinessId]);

  // Validation
  function validateCurrentStep(): boolean {
    setError(null);
    if (currentStep === 1) {
      if (!businessName.trim()) {
        setError('Business Name is required');
        return false;
      }
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email address');
        return false;
      }
    }

    if (currentStep === 2) {
      if (!primaryIndustry.trim()) {
        setError('Please select or specify a Primary Industry');
        return false;
      }
    }

    if (currentStep === 3) {
      const validServices = services.filter((s) => s.name.trim().length > 0);
      if (validServices.length === 0) {
        setError('Please add at least one service offered by your business');
        return false;
      }
    }

    if (currentStep === 4) {
      const validTargets = targetLocations.filter((t) => t.name.trim().length > 0);
      if (validTargets.length === 0) {
        setError('Please specify at least one target location/city');
        return false;
      }
    }

    return true;
  }

  function handleNext() {
    if (validateCurrentStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, 6));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function handleBack() {
    setError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Tag helpers for secondary categories
  function handleAddSecondaryCategory() {
    if (secCategoryInput.trim() && !secondaryCategories.includes(secCategoryInput.trim())) {
      setSecondaryCategories([...secondaryCategories, secCategoryInput.trim()]);
      setSecCategoryInput('');
    }
  }

  function handleRemoveSecondaryCategory(cat: string) {
    setSecondaryCategories(secondaryCategories.filter((c) => c !== cat));
  }

  // Service helpers
  function handleAddService(name = '', description = '') {
    setServices([...services, { name, description }]);
  }

  function handleUpdateService(idx: number, field: keyof ServiceItem, val: string) {
    const next = [...services];
    next[idx] = { ...next[idx], [field]: val };
    setServices(next);
  }

  function handleRemoveService(idx: number) {
    if (services.length <= 1) {
      setServices([{ name: '', description: '' }]);
    } else {
      setServices(services.filter((_, i) => i !== idx));
    }
  }

  // Target Location helpers
  function handleAddTargetLocation(name = '', city = '', state = '') {
    setTargetLocations([...targetLocations, { name, city, state, country: 'India' }]);
  }

  function handleUpdateTargetLocation(idx: number, field: keyof TargetLocItem, val: string) {
    const next = [...targetLocations];
    next[idx] = { ...next[idx], [field]: val };
    setTargetLocations(next);
  }

  function handleRemoveTargetLocation(idx: number) {
    if (targetLocations.length <= 1) {
      setTargetLocations([{ name: '', city: '', state: '', country: 'India' }]);
    } else {
      setTargetLocations(targetLocations.filter((_, i) => i !== idx));
    }
  }

  // Submission
  async function handleComplete(saveDraftOnly = false) {
    if (!saveDraftOnly && !validateCurrentStep()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const filteredServices = services
        .filter((s) => s.name.trim().length > 0)
        .map((s) => ({ name: s.name.trim(), description: s.description.trim() }));

      const filteredTargetLocations = targetLocations
        .filter((t) => t.name.trim().length > 0)
        .map((t) => ({
          name: t.name.trim(),
          city: t.city.trim() || t.name.trim(),
          state: t.state.trim(),
          country: t.country.trim() || 'India',
        }));

      const latNum = latitude.trim() !== '' && !isNaN(Number(latitude)) ? Number(latitude) : undefined;
      const lngNum = longitude.trim() !== '' && !isNaN(Number(longitude)) ? Number(longitude) : undefined;

      const locationPayload = city.trim() || address.trim() || state.trim() || postalCode.trim()
        ? {
            name: existingLocationName || `${businessName.trim()} Main Location`,
            address: address.trim() || undefined,
            city: city.trim() || undefined,
            state: state.trim() || undefined,
            country: country.trim() || 'India',
            postalCode: postalCode.trim() || undefined,
            latitude: latNum,
            longitude: lngNum,
          }
        : undefined;

      let savedBizId = editingBusinessId;

      if (!editingBusinessId) {
        // Create new business with full Phase 2 payload
        const created = await businessApi.create({
          name: businessName.trim(),
          website: website.trim() || undefined,
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
          description: description.trim() || undefined,
          industry: primaryIndustry.trim() || undefined,
          location: locationPayload,
          services: filteredServices,
          targetLocations: filteredTargetLocations,
          settings: {
            primaryIndustry: primaryIndustry.trim() || undefined,
            primaryCategory: primaryCategory.trim() || undefined,
            secondaryCategories: secondaryCategories,
          },
        });
        savedBizId = created.id;
      } else {
        // Update existing business with full payload including location, services, targetLocations, settings
        await businessApi.update(editingBusinessId, {
          name: businessName.trim(),
          website: website.trim() || undefined,
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
          description: description.trim() || undefined,
          industry: primaryIndustry.trim() || undefined,
          location: locationPayload,
          services: filteredServices,
          targetLocations: filteredTargetLocations,
          settings: {
            primaryIndustry: primaryIndustry.trim() || undefined,
            primaryCategory: primaryCategory.trim() || undefined,
            secondaryCategories: secondaryCategories,
          },
        });
        savedBizId = editingBusinessId;
      }

      if (savedBizId) {
        // Update onboarding tracker
        await businessApi.updateOnboarding(savedBizId, {
          currentStep,
          completed: !saveDraftOnly,
        });

        // Redirect to Business SEO Dashboard
        navigate(`/businesses/${savedBizId}/dashboard`);
      }
    } catch (err: unknown) {
      console.error('Failed to save onboarding', err);
      const msg = err instanceof Error ? err.message : 'Failed to complete setup';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const STEPS = [
    { number: 1, label: 'Business Info' },
    { number: 2, label: 'Industry & Categories' },
    { number: 3, label: 'Services' },
    { number: 4, label: 'Target Locations' },
    { number: 5, label: 'Physical Location' },
    { number: 6, label: 'Review & Finish' },
  ];

  if (initialLoading) {
    return (
      <DashboardLayout title="Onboard Business" subtitle="Loading configuration...">
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
          Loading business onboarding data...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Business Onboarding"
      subtitle="Complete your local SEO profile to initialize rank tracking and optimization"
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Businesses', to: '/businesses' },
        { label: editingBusinessId ? 'Edit Onboarding' : 'New Business Onboarding' },
      ]}
    >
      <div className="onboarding-container">
        {/* Wizard Stepper Progress Bar */}
        <div className="wizard-stepper">
          {STEPS.map((s) => {
            const isCompleted = s.number < currentStep;
            const isCurrent = s.number === currentStep;
            const isClickable = !!editingBusinessId || s.number <= currentStep;
            return (
              <div
                key={s.number}
                className={`wizard-step-item ${isCurrent ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
                onClick={() => {
                  if (isClickable) setCurrentStep(s.number);
                }}
              >
                <div className="wizard-step-circle">
                  {isCompleted ? <IconCheck style={{ width: 16, height: 16 }} /> : s.number}
                </div>
                <div className="wizard-step-label">{s.label}</div>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 20 }}>
            <span></span>
            <span>{error}</span>
          </div>
        )}

        {/* Wizard Body Cards */}
        <div className="card wizard-card">
          {/* STEP 1: Business Information */}
          {currentStep === 1 && (
            <div className="wizard-step-content">
              <h2 className="step-title">Step 1: Business Information</h2>
              <p className="step-subtitle">
                Enter core details about your company. This will be the foundation of your local presence.
              </p>

              <div className="form-group">
                <label className="form-label" htmlFor="biz-name">
                  Business Name <span className="required-star">*</span>
                </label>
                <input
                  id="biz-name"
                  type="text"
                  className="form-input"
                  placeholder="e.g. Tisha LED Walls"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="biz-website">
                  Website URL
                </label>
                <input
                  id="biz-website"
                  type="text"
                  className="form-input"
                  placeholder="e.g. https://tishaledwalls.pages.dev"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
                <span className="form-hint">Used for future Website SEO Audits & Local Schema validation.</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="biz-phone">
                    Phone Number
                  </label>
                  <input
                    id="biz-phone"
                    type="tel"
                    className="form-input"
                    placeholder="e.g. +91 98765 43210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="biz-email">
                    Public Contact Email
                  </label>
                  <input
                    id="biz-email"
                    type="email"
                    className="form-input"
                    placeholder="e.g. contact@business.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="biz-desc">
                  Business Description
                </label>
                <textarea
                  id="biz-desc"
                  className="form-textarea"
                  rows={4}
                  placeholder="Briefly describe what your business specializes in, your unique value proposition, and customer base."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* STEP 2: Industry & Categories */}
          {currentStep === 2 && (
            <div className="wizard-step-content">
              <h2 className="step-title">Step 2: Industry & Categories</h2>
              <p className="step-subtitle">
                Accurate categorization ensures relevant keyword ranking suggestions and Google Business Profile alignment.
              </p>

              <div className="form-group">
                <label className="form-label" htmlFor="biz-industry">
                  Primary Industry <span className="required-star">*</span>
                </label>
                <select
                  id="biz-industry"
                  className="form-select"
                  value={primaryIndustry}
                  onChange={(e) => setPrimaryIndustry(e.target.value)}
                >
                  <option value="">-- Select an Industry --</option>
                  {INDUSTRY_PRESETS.map((ind) => (
                    <option key={ind} value={ind}>
                      {ind}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="biz-category">
                  Primary Category
                </label>
                <input
                  id="biz-category"
                  type="text"
                  className="form-input"
                  placeholder="e.g. Audio Visual Equipment Rental, Wedding Planner, Roofing Contractor"
                  value={primaryCategory}
                  onChange={(e) => setPrimaryCategory(e.target.value)}
                />
                <span className="form-hint">Matches your primary Google Business Profile category.</span>
              </div>

              <div className="form-group">
                <label className="form-label">Secondary Categories</label>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Event Technology, Stage Production"
                    value={secCategoryInput}
                    onChange={(e) => setSecCategoryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSecondaryCategory();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleAddSecondaryCategory}
                  >
                    Add
                  </button>
                </div>

                <div className="tags-container">
                  {secondaryCategories.map((cat) => (
                    <span key={cat} className="badge badge-blue tag-chip">
                      {cat}
                      <button
                        type="button"
                        className="tag-remove-btn"
                        onClick={() => handleRemoveSecondaryCategory(cat)}
                        title="Remove category"
                      >
                        
                      </button>
                    </span>
                  ))}
                  {secondaryCategories.length === 0 && (
                    <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                      No secondary categories added yet.
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Services */}
          {currentStep === 3 && (
            <div className="wizard-step-content">
              <h2 className="step-title">Step 3: Define Services</h2>
              <p className="step-subtitle">
                List the core products and services your business sells. These will drive AI recommendations and local keyword targeting.
              </p>

              {/* Quick Preset Suggestions */}
              {primaryIndustry && PRESET_SERVICES_MAP[primaryIndustry] && (
                <div className="preset-suggestions-box">
                  <span className="preset-title"> Popular for {primaryIndustry}:</span>
                  <div className="preset-chips">
                    {PRESET_SERVICES_MAP[primaryIndustry].map((presetName) => {
                      const alreadyAdded = services.some((s) => s.name.toLowerCase() === presetName.toLowerCase());
                      return (
                        <button
                          key={presetName}
                          type="button"
                          className={`preset-chip-btn ${alreadyAdded ? 'added' : ''}`}
                          disabled={alreadyAdded}
                          onClick={() => handleAddService(presetName, `Professional ${presetName} services.`)}
                        >
                          {alreadyAdded ? ' Added' : `+ ${presetName}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="dynamic-list">
                {services.map((svc, idx) => (
                  <div key={idx} className="dynamic-item-card">
                    <div className="dynamic-item-header">
                      <span className="dynamic-badge">Service #{idx + 1}</span>
                      <button
                        type="button"
                        className="btn-link-danger"
                        onClick={() => handleRemoveService(idx)}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14 }}>
                      <div>
                        <label className="form-label">Service Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="e.g. LED Wall Rental"
                          value={svc.name}
                          onChange={(e) => handleUpdateService(idx, 'name', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Service Description</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="e.g. High-definition curved and flat P2/P3 panels for corporate events"
                          value={svc.description}
                          onChange={(e) => handleUpdateService(idx, 'description', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => handleAddService()}
                style={{ marginTop: 12 }}
              >
                + Add Another Service
              </button>
            </div>
          )}

          {/* STEP 4: Target Locations */}
          {currentStep === 4 && (
            <div className="wizard-step-content">
              <h2 className="step-title">Step 4: Target Locations</h2>
              <p className="step-subtitle">
                Specify the cities and surrounding geographic markets where you want to attract local customers and rank in search.
              </p>

              <div className="dynamic-list">
                {targetLocations.map((tLoc, idx) => (
                  <div key={idx} className="dynamic-item-card">
                    <div className="dynamic-item-header">
                      <span className="dynamic-badge">Target Market #{idx + 1}</span>
                      <button
                        type="button"
                        className="btn-link-danger"
                        onClick={() => handleRemoveTargetLocation(idx)}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 14 }}>
                      <div>
                        <label className="form-label">Location / City Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="e.g. Gurugram, Delhi, Noida"
                          value={tLoc.name}
                          onChange={(e) => {
                            handleUpdateTargetLocation(idx, 'name', e.target.value);
                            if (!tLoc.city) {
                              handleUpdateTargetLocation(idx, 'city', e.target.value);
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label className="form-label">State / Province</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="e.g. Haryana, Delhi NCR"
                          value={tLoc.state}
                          onChange={(e) => handleUpdateTargetLocation(idx, 'state', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">Country</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="e.g. India"
                          value={tLoc.country}
                          onChange={(e) => handleUpdateTargetLocation(idx, 'country', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => handleAddTargetLocation()}
                style={{ marginTop: 12 }}
              >
                + Add Another Target Location
              </button>
            </div>
          )}

          {/* STEP 5: Physical Business Location */}
          {currentStep === 5 && (
            <div className="wizard-step-content">
              <h2 className="step-title">Step 5: Business Physical Location</h2>
              <p className="step-subtitle">
                Your registered physical address for Google Maps pin positioning, NAP (Name-Address-Phone) consistency, and distance ranking.
              </p>

              <div className="form-group">
                <label className="form-label" htmlFor="loc-address">
                  Street Address
                </label>
                <input
                  id="loc-address"
                  type="text"
                  className="form-input"
                  placeholder="e.g. Sector 48, Sohna Road"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="loc-city">
                    City
                  </label>
                  <input
                    id="loc-city"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Gurugram"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="loc-state">
                    State
                  </label>
                  <input
                    id="loc-state"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Haryana"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="loc-postal">
                    Postal Code / PIN
                  </label>
                  <input
                    id="loc-postal"
                    type="text"
                    className="form-input"
                    placeholder="e.g. 122018"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="loc-country">
                    Country
                  </label>
                  <input
                    id="loc-country"
                    type="text"
                    className="form-input"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="loc-lat">
                    Latitude (Optional)
                  </label>
                  <input
                    id="loc-lat"
                    type="number"
                    step="any"
                    className="form-input"
                    placeholder="e.g. 28.4595"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="loc-lng">
                    Longitude (Optional)
                  </label>
                  <input
                    id="loc-lng"
                    type="number"
                    step="any"
                    className="form-input"
                    placeholder="e.g. 77.0266"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 6: Review & Finish */}
          {currentStep === 6 && (
            <div className="wizard-step-content">
              <h2 className="step-title">Step 6: Review & Finalize</h2>
              <p className="step-subtitle">
                Confirm your business configuration before initializing your Local SEO Project.
              </p>

              <div className="review-grid">
                {/* 1. Business Info */}
                <div className="review-card">
                  <div className="review-card-header">
                    <h4> Business Information</h4>
                    <button type="button" className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', fontSize: '13px' }} onClick={() => setCurrentStep(1)}>
                      <IconEdit style={{ width: 14, height: 14 }} />
                      Edit
                    </button>
                  </div>
                  <div className="review-row">
                    <span className="review-lbl">Name:</span>
                    <span className="review-val font-semibold">{businessName}</span>
                  </div>
                  <div className="review-row">
                    <span className="review-lbl">Website:</span>
                    <span className="review-val">{website || 'Not provided'}</span>
                  </div>
                  <div className="review-row">
                    <span className="review-lbl">Phone:</span>
                    <span className="review-val">{phone || 'Not provided'}</span>
                  </div>
                  <div className="review-row">
                    <span className="review-lbl">Email:</span>
                    <span className="review-val">{email || 'Not provided'}</span>
                  </div>
                  {description && (
                    <div className="review-row-block">
                      <span className="review-lbl">Description:</span>
                      <p className="review-val-text">{description}</p>
                    </div>
                  )}
                </div>

                {/* 2. Industry */}
                <div className="review-card">
                  <div className="review-card-header">
                    <h4> Industry & Category</h4>
                    <button type="button" className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', fontSize: '13px' }} onClick={() => setCurrentStep(2)}>
                      <IconEdit style={{ width: 14, height: 14 }} />
                      Edit
                    </button>
                  </div>
                  <div className="review-row">
                    <span className="review-lbl">Primary Industry:</span>
                    <span className="review-val font-semibold">{primaryIndustry || 'Not selected'}</span>
                  </div>
                  <div className="review-row">
                    <span className="review-lbl">Primary Category:</span>
                    <span className="review-val">{primaryCategory || 'Not specified'}</span>
                  </div>
                  <div className="review-row-block">
                    <span className="review-lbl">Secondary Categories:</span>
                    <div className="tags-container" style={{ marginTop: 4 }}>
                      {secondaryCategories.length > 0 ? (
                        secondaryCategories.map((c) => (
                          <span key={c} className="badge badge-blue">
                            {c}
                          </span>
                        ))
                      ) : (
                        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>None</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 3. Services */}
                <div className="review-card">
                  <div className="review-card-header">
                    <h4> Services ({services.filter((s) => s.name.trim()).length})</h4>
                    <button type="button" className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', fontSize: '13px' }} onClick={() => setCurrentStep(3)}>
                      <IconEdit style={{ width: 14, height: 14 }} />
                      Edit
                    </button>
                  </div>
                  <ul className="review-list">
                    {services
                      .filter((s) => s.name.trim())
                      .map((s, i) => (
                        <li key={i}>
                          <strong>{s.name}</strong>
                          {s.description && <span>  {s.description}</span>}
                        </li>
                      ))}
                  </ul>
                </div>

                {/* 4. Target Locations */}
                <div className="review-card">
                  <div className="review-card-header">
                    <h4> Target Locations ({targetLocations.filter((t) => t.name.trim()).length})</h4>
                    <button type="button" className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', fontSize: '13px' }} onClick={() => setCurrentStep(4)}>
                      <IconEdit style={{ width: 14, height: 14 }} />
                      Edit
                    </button>
                  </div>
                  <div className="tags-container" style={{ marginTop: 6 }}>
                    {targetLocations
                      .filter((t) => t.name.trim())
                      .map((t, i) => (
                        <span key={i} className="badge badge-green">
                           {t.name} {t.state ? `(${t.state})` : ''}
                        </span>
                      ))}
                  </div>
                </div>

                {/* 5. Physical Location */}
                <div className="review-card" style={{ gridColumn: '1 / -1' }}>
                  <div className="review-card-header">
                    <h4> Physical Address & Headquarters</h4>
                    <button type="button" className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', fontSize: '13px' }} onClick={() => setCurrentStep(5)}>
                      <IconEdit style={{ width: 14, height: 14 }} />
                      Edit
                    </button>
                  </div>
                  <div className="review-row">
                    <span className="review-lbl">Address:</span>
                    <span className="review-val">
                      {[address, city, state, postalCode, country].filter(Boolean).join(', ') ||
                        'Not provided'}
                    </span>
                  </div>
                  {latitude && longitude && (
                    <div className="review-row">
                      <span className="review-lbl">Coordinates:</span>
                      <span className="review-val">
                        {latitude}, {longitude}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stepper Footer Controls */}
          <div className="wizard-footer">
            <div>
              {currentStep > 1 && (
                <button
                  type="button"
                  id="wizard-back-btn"
                  className="btn btn-secondary"
                  onClick={handleBack}
                  disabled={loading}
                >
                   Back
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                id="wizard-save-draft-btn"
                className="btn btn-secondary"
                onClick={() => handleComplete(true)}
                disabled={loading || !businessName.trim()}
              >
                {loading ? 'Saving...' : 'Save Draft'}
              </button>

              {currentStep < 6 ? (
                <button
                  type="button"
                  id="wizard-next-btn"
                  className="btn btn-primary"
                  onClick={handleNext}
                >
                  Next Step 
                </button>
              ) : (
                <button
                  type="button"
                  id="wizard-complete-btn"
                  className="btn btn-primary"
                  onClick={() => handleComplete(false)}
                  disabled={loading}
                >
                  {loading ? 'Initializing Project...' : ' Complete Setup'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
